import { CreateQuestionarioCommandDto } from '../api/models/create-questionario-command.dto';
import { CreateQuestionarioAlternativaCommandDto } from '../api/models/create-questionario-alternativa-command.dto';
import { CreateQuestionarioPerguntaCommandDto } from '../api/models/create-questionario-pergunta-command.dto';
import { QuestionarioAlternativaResponseDto } from '../api/models/questionario-alternativa-response.dto';
import { QuestionarioPerguntaResponseDto } from '../api/models/questionario-pergunta-response.dto';
import { QuestionarioResponseDto } from '../api/models/questionario-response.dto';
import {
  QUESTIONARIO_STATUS_PUBLICADO,
  QUESTIONARIO_STATUS_RASCUNHO,
} from '../api/models/questionario-status-api';
import { TIPO_PERGUNTA_ALTERNATIVA_API } from '../api/models/tipo-pergunta-api';
import { QuestionarioStatus } from '../enum/questionario-status.enum';
import { TipoPergunta } from '../enum/tipo-pergunta.enum';
import { TipoQuestionario } from '../enum/tipo-questionario.enum';
import {
  QuestionarioAlternativa,
  QuestionarioFormValue,
  QuestionarioPergunta,
} from '../models/questionario-form.model';
import { CriarQuestionarioRequest, Questionario } from '../models/questionario.model';

/**
 * A API devolve o código ("P"/"R") e o mock usa a descrição ("Publicado"/"Rascunho") — os dois
 * formatos são aceitos, normalizados. Mesmo padrão de `mapCodigoStatusToCampanhaStatus`.
 */
const QUESTIONARIO_STATUS_MAP: Record<string, QuestionarioStatus> = {
  [QUESTIONARIO_STATUS_PUBLICADO]: QuestionarioStatus.Publicado,
  [QUESTIONARIO_STATUS_RASCUNHO]: QuestionarioStatus.Rascunho,
  PUBLICADO: QuestionarioStatus.Publicado,
  RASCUNHO: QuestionarioStatus.Rascunho,
};

export function mapQuestionarioStatus(status: string | null | undefined): QuestionarioStatus {
  const normalizado = status?.trim().toUpperCase() ?? '';
  return QUESTIONARIO_STATUS_MAP[normalizado] ?? QuestionarioStatus.Rascunho;
}

export function mapQuestionarioDto(dto: QuestionarioResponseDto): Questionario {
  const status = mapQuestionarioStatus(dto.status);

  return {
    id: dto.idQuestionario ?? 0,
    nome: dto.nome ?? '',
    status,
    ultimaAtualizacao: dto.dtUltAtu ?? dto.dtInclusao ?? '',
  };
}

export function mapAlternativaResponse(
  dto: QuestionarioAlternativaResponseDto,
): QuestionarioAlternativa {
  return {
    id: dto.idAlternativa,
    texto: dto.texto ?? '',
    pontos: dto.pontuacao ?? 0,
  };
}

export function mapPerguntaResponse(
  dto: QuestionarioPerguntaResponseDto,
  alternativas: QuestionarioAlternativaResponseDto[],
): QuestionarioPergunta {
  return {
    id: dto.idPergunta,
    texto: dto.texto ?? '',
    tipo: TipoPergunta.Alternativa,
    // Removido por decisão do PO — peso removido, ver plano
    // 2026-08-11-questionario-erro-exclusao-e-remove-peso.
    // peso: dto.peso ?? 0,
    alternativas: alternativas
      .filter((alt) => alt.idPergunta === dto.idPergunta)
      .map(mapAlternativaResponse),
  };
}

export function mapQuestionarioResponseToFormValue(
  dto: QuestionarioResponseDto,
  perguntas: QuestionarioPerguntaResponseDto[],
  alternativas: QuestionarioAlternativaResponseDto[],
): QuestionarioFormValue {
  const perguntasOrdenadas = [...perguntas].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));

  return {
    id: dto.idQuestionario,
    nome: dto.nome ?? '',
    tipo: TipoQuestionario.Suitability,
    status: mapQuestionarioStatus(dto.status),
    // Respostas sem o campo mantêm o comportamento anterior: editável.
    permiteEdicao: dto.permiteEdicao ?? true,
    motivoBloqueioEdicao: dto.motivoBloqueioEdicao ?? '',
    perguntas: perguntasOrdenadas.map((pergunta) => mapPerguntaResponse(pergunta, alternativas)),
    perfis: [],
  };
}

function mapTipoPerguntaToApi(tipo: TipoPergunta): string {
  if (tipo === TipoPergunta.Alternativa) {
    return TIPO_PERGUNTA_ALTERNATIVA_API;
  }

  return TIPO_PERGUNTA_ALTERNATIVA_API;
}

export function mapPerguntaFormValueToCommand(
  idQuestionario: number,
  pergunta: QuestionarioPergunta,
  ordem: number,
): CreateQuestionarioPerguntaCommandDto {
  return {
    idQuestionario,
    texto: pergunta.texto,
    tipo: mapTipoPerguntaToApi(pergunta.tipo),
    // Removido por decisão do PO — peso removido, ver plano
    // 2026-08-11-questionario-erro-exclusao-e-remove-peso.
    // peso: pergunta.peso,
    ordem,
  };
}

export function mapAlternativaFormValueToCommand(
  idPergunta: number,
  alternativa: QuestionarioAlternativa,
): CreateQuestionarioAlternativaCommandDto {
  return {
    idPergunta,
    texto: alternativa.texto,
    pontuacao: alternativa.pontos,
  };
}

export function mapQuestionarioFormValueToCommand(
  form: QuestionarioFormValue,
  status: string,
): CreateQuestionarioCommandDto {
  return {
    nome: form.nome,
    status,
  };
}

/**
 * Monta o corpo de `POST /v1/Questionario` a partir do formulário. A `ordem` vem da posição na
 * lista, que já reflete a tela — inclusive depois de um arrastar e soltar, porque o FormArray é
 * reordenado junto. Como no fluxo incremental, a API espera a ordem começando em 1, e o índice
 * é 0-based.
 *
 * Só entram em `pontuacoes` os perfis com alguma pontuação preenchida; um perfil apenas
 * associado, sem faixa, não representa regra alguma para a API.
 */
export function mapQuestionarioFormValueToCriarRequest(
  form: QuestionarioFormValue,
  status: string,
): CriarQuestionarioRequest {
  return {
    nome: form.nome.trim(),
    status,
    perguntas: form.perguntas.map((pergunta, indice) => ({
      texto: pergunta.texto.trim(),
      tipo: mapTipoPerguntaToApi(pergunta.tipo),
      ordem: indice + 1,
      alternativas: pergunta.alternativas.map((alternativa, indiceAlternativa) => ({
        texto: alternativa.texto.trim(),
        pontuacao: alternativa.pontos,
        ordem: indiceAlternativa + 1,
      })),
    })),
    pontuacoes: form.perfis
      .filter((perfil) => perfil.pontuacaoMinima != null || perfil.pontuacaoMaxima != null)
      .map((perfil) => ({
        idPerfilInvestimento: perfil.idPerfilInvestimento,
        pontuacaoMinima: perfil.pontuacaoMinima,
        pontuacaoMaxima: perfil.pontuacaoMaxima,
      })),
  };
}
